import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  BULK_SCOPE,
  getBulkDisplayCount,
  getBulkScopeLabel,
} from "../../helper/bulkSelection/bulkSelectionModes";
import { openRequestTrackingPage } from "../../helper/common/openRequestTrackingPage";

/**
 * Shared MUI confirmation modal for V2 bulk actions.
 * Supports a post-submit success state with Request Tracking CTA
 * (same pattern as csvUploadModal).
 */
export default function BulkActionConfirmModalV2({
  open = false,
  title = "Confirm bulk action",
  actionLabel = "Confirm",
  description,
  infoNote = null,
  scope = BULK_SCOPE.SELECTED,
  selectedIdsCount = 0,
  totalCount = 0,
  excludeIdsCount = 0,
  confirmColor = "primary",
  onConfirm,
  onSecondaryConfirm,
  secondaryActionLabel,
  onCancel,
  zIndex = 1400,
  isSuccess = false,
  successTitle = "Request Submitted",
  successMessage = "Your bulk action request has been submitted successfully. You can track it on the Request Tracking page.",
  onGoToTracking,
}) {
  const displayCount = getBulkDisplayCount({
    scope,
    selectedIdsCount,
    totalCount,
    excludeIdsCount,
  });
  const scopeLabel = getBulkScopeLabel({
    scope,
    selectedIdsCount,
    totalCount,
    excludeIdsCount,
  });

  const openRequestTracking = () => {
    if (onGoToTracking) {
      onGoToTracking();
      return;
    }
    openRequestTrackingPage();
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      disableScrollLock
      sx={{ zIndex }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 12px 40px rgba(16, 24, 40, 0.16)",
        },
      }}
    >
      {isSuccess ? (
        <>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              gap={2}
              py={3}
            >
              <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
              <Typography variant="h6" fontWeight={700}>
                {successTitle}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 420, fontSize: "14px" }}
              >
                {successMessage}
              </Typography>
              <Button
                variant="contained"
                endIcon={<OpenInNewIcon />}
                onClick={openRequestTracking}
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  px: 2.5,
                  background:
                    "linear-gradient(135deg, #5B4BDB 0%, #7C6FF0 100%)",
                }}
              >
                Go to Request Tracking
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, justifyContent: "center" }}>
            <Button
              onClick={onCancel}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                borderColor: "grey.400",
                color: "#333",
                minWidth: 96,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  mt: 0.25,
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: "#F3EFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#6B4EFF", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111" }}>
                  {title}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
                >
                  {scopeLabel}
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pb: 1 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                bgcolor: "#FAFAFB",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#333", lineHeight: 1.5 }}>
                {description || (
                  <>
                    This will apply to{" "}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {displayCount.toLocaleString()}
                    </Box>{" "}
                    record(s).
                  </>
                )}
              </Typography>
            </Box>
            {infoNote ? (
              <Typography
                sx={{ fontSize: "11px", color: "error.main", mt: 1.25, lineHeight: 1.4 }}
              >
                * {infoNote}
              </Typography>
            ) : null}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            <Button
              onClick={onCancel}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                borderColor: "grey.400",
                color: "#333",
                minWidth: 96,
              }}
            >
              Cancel
            </Button>
            {secondaryActionLabel ? (
              <Button
                onClick={onSecondaryConfirm || onConfirm}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  minWidth: 140,
                  borderColor: "grey.400",
                  color: "#333",
                }}
              >
                {secondaryActionLabel}
              </Button>
            ) : null}
            <Button
              onClick={onConfirm}
              variant="contained"
              color={confirmColor}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                minWidth: 120,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {actionLabel}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
